/**
 * Recurring Schedules List Component
 * Displays and manages recurring schedules for a company
 */

import { useState, useEffect } from 'react';
import type { UserProfile } from '../../../../shared/types';
import {
  getCompanySchedules,
  deleteRecurringSchedule,
  updateRecurringSchedule,
  assignRecurringSchedule,
  type RecurringScheduleWithId,
} from '../../services/recurringSchedule.service';
import { getCompanyUsers } from '../../services/user.service';
import { getUserNameById } from '@shared/utils/formatting';
import { getErrorMessage } from '@shared/utils/error';
import { ErrorMessage } from '../ErrorMessage';
import { DeleteRecurringScheduleModal, EditRecurringScheduleModal } from '../modals';
import { ItemCard } from '../ItemCard';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { Toast } from '../common';
import { useToast } from '../../hooks/useToast';
import { EMPTY_STATE_STYLE } from '../../styles/components';

interface RecurringSchedulesListProps {
  companyId: string;
  refreshTrigger?: number;
  companyTimezone?: string;
}

export function RecurringSchedulesList({ companyId, refreshTrigger = 0, companyTimezone }: RecurringSchedulesListProps) {
  const { toasts, dismissToast } = useToast();
  const [schedules, setSchedules] = useState<RecurringScheduleWithId[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assigningScheduleId, setAssigningScheduleId] = useState<string | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<RecurringScheduleWithId | null>(null);
  const [deletingSchedule, setDeletingSchedule] = useState<RecurringScheduleWithId | null>(null);
  const [noManagerConfirm, setNoManagerConfirm] = useState<{ scheduleId: string; staffId: string; name: string } | null>(null);

  const managers = users.filter((u) => u.userType === 'manager' && !u.isArchived);
  const staff = users.filter((u) => u.userType === 'staff' && !u.isArchived);

  const loadData = async () => {
    try {
      setLoading(true);
      const [schedulesData, usersData] = await Promise.all([
        getCompanySchedules(companyId),
        getCompanyUsers(companyId),
      ]);
      setSchedules(schedulesData);
      setUsers(usersData);
      setError('');
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [companyId, refreshTrigger]);

  const handleToggleActive = async (schedule: RecurringScheduleWithId) => {
    try {
      await updateRecurringSchedule({
        scheduleId: schedule.id,
        companyId,
        isActive: !schedule.isActive,
      });
      await loadData();
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to update schedule');
    }
  };

  const handleDelete = async (scheduleId: string) => {
    try {
      await deleteRecurringSchedule(scheduleId, companyId);
      setDeletingSchedule(null);
      await loadData();
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to delete schedule');
      throw err; // Re-throw so modal knows it failed
    }
  };

  const getUserName = (userId: string) => {
    return getUserNameById(userId, users, 'Unknown');
  };

  const handleAssignToManager = async (scheduleId: string, managerId: string) => {
    setAssigningScheduleId(scheduleId);
    try {
      await assignRecurringSchedule({
        scheduleId,
        companyId,
        assignedManagerId: managerId,
      });
      await loadData();
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to assign schedule to manager');
    } finally {
      setAssigningScheduleId(null);
    }
  };

  const handleAssignToStaff = async (scheduleId: string, staffId: string) => {
    const hasManager = managers.some((m) => m.managedStaffIds?.includes(staffId));
    if (!hasManager) {
      const staffMember = staff.find((s) => s.uid === staffId);
      const name = staffMember ? `${staffMember.firstName} ${staffMember.lastName}` : 'This staff member';
      setNoManagerConfirm({ scheduleId, staffId, name });
      return;
    }
    await doAssignToStaff(scheduleId, staffId);
  };

  const doAssignToStaff = async (scheduleId: string, staffId: string) => {
    setAssigningScheduleId(scheduleId);
    try {
      await assignRecurringSchedule({
        scheduleId,
        companyId,
        userIds: [staffId],
      });
      await loadData();
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to assign schedule to staff');
    } finally {
      setAssigningScheduleId(null);
    }
  };

  const handleUnassign = async (scheduleId: string) => {
    setAssigningScheduleId(scheduleId);
    try {
      await assignRecurringSchedule({
        scheduleId,
        companyId,
      });
      await loadData();
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to unassign schedule');
    } finally {
      setAssigningScheduleId(null);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>Loading schedules...</div>;
  }

  return (
    <div>
      {error && <ErrorMessage message={error} />}

      {/* Schedules List */}
      {schedules.length === 0 ? (
        <div style={EMPTY_STATE_STYLE}>
          No recurring schedules yet. Click "New Schedule" to get started.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {schedules.map((schedule) => (
            <ItemCard
              key={schedule.id}
              item={schedule}
              viewerRole="company"
              managers={managers}
              staff={staff}
              onAssignToManager={handleAssignToManager}
              onAssignToStaff={handleAssignToStaff}
              onUnassign={handleUnassign}
              onEditSchedule={setEditingSchedule}
              onToggleActive={handleToggleActive}
              onDeleteSchedule={setDeletingSchedule}
              isAssigning={assigningScheduleId === schedule.id}
              getUserName={getUserName}
              companyTimezone={companyTimezone}
            />
          ))}
        </div>
      )}

      {/* Edit Schedule Modal */}
      <EditRecurringScheduleModal
        isOpen={!!editingSchedule}
        onClose={() => setEditingSchedule(null)}
        schedule={editingSchedule}
        onSuccess={() => {
          setEditingSchedule(null);
          loadData();
        }}
      />

      {/* Delete Schedule Modal */}
      <DeleteRecurringScheduleModal
        isOpen={!!deletingSchedule}
        onClose={() => setDeletingSchedule(null)}
        scheduleTitle={deletingSchedule?.title || ''}
        onConfirm={() => handleDelete(deletingSchedule!.id)}
      />

      <ConfirmationDialog
        isOpen={!!noManagerConfirm}
        title="No Manager Assigned"
        message={`${noManagerConfirm?.name} has no assigned manager. Time entries will only be visible to the company administrator — no manager will receive alerts or be able to approve their hours.`}
        confirmLabel="Assign Anyway"
        confirmStyle="warning"
        onConfirm={async () => {
          if (noManagerConfirm) {
            setNoManagerConfirm(null);
            await doAssignToStaff(noManagerConfirm.scheduleId, noManagerConfirm.staffId);
          }
        }}
        onCancel={() => setNoManagerConfirm(null)}
      />

      <Toast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
